import { FC, useMemo, useState } from 'react';
import { Box, Typography, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded';
import type { HotelItem } from '../../../../../types/hotel.types';
import CustomButton from '../../../../../common/CustomButton';
import hotelImageFallback from '../../../../../assets/images/TripMockDetails.jpg';
import { useIsMobile } from '../../../../../hooks/useWindowWidth';
import styles from './HotelResultCard.module.scss';
import { LocationIcon } from '../../../../../components/Icons/LocationIcon';
import { GoogleLocationIcon } from '../../../../../components/Icons/GoogleLocationIcon';

export interface HotelResultCardProps {
  hotel: HotelItem;
  onSave: (hotel: HotelItem) => void;
}

function buildMapsLink(hotel: HotelItem): string | null {
  const addressName = hotel.address?.name?.trim();
  const lat = hotel.address?.latitude;
  const lng = hotel.address?.longitude;
  if (lat != null && lng != null && (lat !== 0 || lng !== 0)) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }
  if (addressName) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressName)}`;
  }
  return null;
}

function formatRating(rating: number | undefined): string {
  if (rating == null || Number.isNaN(rating)) return '';
  return rating.toFixed(1);
}

function renderStars(rating: number | undefined) {
  const score = Math.max(0, Math.min(5, Math.round(rating ?? 0)));
  const stars = [];
  for (let i = 0; i < 5; i += 1) {
    stars.push(
      i < score ? (
        <StarRoundedIcon
          key={i}
          className={styles.starFilled}
          fontSize="small"
        />
      ) : (
        <StarOutlineRoundedIcon
          key={i}
          className={styles.starEmpty}
          fontSize="small"
        />
      )
    );
  }

  return stars;
}

const HotelResultCard: FC<HotelResultCardProps> = ({ hotel, onSave }) => {
  const [expanded, setExpanded] = useState(false);
  const isMobile = useIsMobile();
  const ratingText = formatRating(hotel.rating);
  const addressName = hotel.address?.name?.trim() ?? '';
  const mapsLink = buildMapsLink(hotel);

  const reviewsSnippet = useMemo(() => {
    const n = hotel.user_ratings_total;

    if (n == null || n < 0) return '';

    const formatted = n.toLocaleString('de-DE');

    return `${formatted} ${n === 1 ? 'review' : 'reviews'}`;
  }, [hotel.user_ratings_total]);

  const toggleExpand = () => setExpanded(v => !v);

  const renderToggleControl = () => (
    <Box
      className={styles.toggleRow}
      onClick={toggleExpand}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();

          toggleExpand();
        }
      }}
    >
      <Typography variant="h11">{expanded ? 'Show less' : 'Show more'}</Typography>

      <ExpandMoreIcon
        sx={{
          fontSize: 20,

          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',

          transition: 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />
    </Box>
  );

  const renderRatingDesktop = () =>
    (hotel.rating != null && hotel.rating > 0) || ratingText ? (
      <Box className={styles.ratingRow}>
        <Box
          className={styles.starsRow}
          aria-label={ratingText ? `Rating ${ratingText} of 5` : undefined}
        >
          {renderStars(hotel.rating)}
        </Box>

        {ratingText ? (
          <Typography
            component="span"
            className={styles.ratingValue}
          >
            {ratingText}
          </Typography>
        ) : null}
      </Box>
    ) : null;

  const renderRatingMobile = () =>
    (hotel.rating != null && hotel.rating > 0) || ratingText ? (
      <Box className={styles.ratingRowMobile}>
        <StarRoundedIcon
          className={styles.starSingleMobile}
          fontSize="small"
          aria-hidden
        />

        {ratingText ? (
          <Typography
            component="span"
            className={styles.ratingValueMobile}
          >
            {ratingText}
          </Typography>
        ) : null}

        {reviewsSnippet ? (
          <Typography
            component="span"
            className={styles.reviewsMuted}
          >
            ({reviewsSnippet})
          </Typography>
        ) : null}
      </Box>
    ) : null;

  return (
    <Box className={styles.card}>
      <Box className={styles.summaryRow}>
        <Box
          className={styles.thumb}
          sx={{ backgroundImage: `url(${hotelImageFallback})` }}
          aria-hidden
        ></Box>

        <Box className={styles.summaryBody}>
          <Typography
            variant="h9Semibold"
            className={styles.hotelName}
          >
            {hotel.name || '—'}
          </Typography>

          {isMobile ? renderRatingMobile() : renderRatingDesktop()}

          {addressName ? (
            <Box className={styles.addressRow}>
              <LocationIcon className={styles.addressIcon} size={20} />

              <Typography
                component="span"
                className={styles.addressText}
              >
                {addressName}
              </Typography>
            </Box>
          ) : null}

          {!isMobile ? renderToggleControl() : null}
        </Box>

        {!isMobile ? (
          <Box className={styles.asideColumn}>
            <Box className={styles.priceBlock}>
              <Typography className={styles.priceLabel}>from</Typography>

              <Typography className={styles.priceValue}>{hotel.price || '—'}</Typography>
            </Box>

            <Box className={styles.actions}>
              <CustomButton
                variant="contained"
                btnType="button"
                onClick={() => onSave(hotel)}
                customClass={styles.saveButton}
              >
                Save
              </CustomButton>
            </Box>
          </Box>
        ) : null}

        {isMobile ? (
          <Box className={styles.mobileBottomBar}>
            {renderToggleControl()}

            <CustomButton
              variant="contained"
              btnType="button"
              onClick={() => onSave(hotel)}
              customClass={styles.saveButtonMobile}
            >
              Save
            </CustomButton>
          </Box>
        ) : null}
      </Box>

      <Box className={styles.detailsWrap}>
        <Collapse
          in={expanded}
          timeout={280}
          unmountOnExit={false}
        >
          <Box className={styles.details}>
            <Typography
              variant="h9Bold"
              className={styles.detailsHeading}
            >
              Details
            </Typography>

            {hotel.description ? (
              <Box className={styles.detailBlock}>
                <Typography
                  component="span"
                  className={styles.detailBlockLabel}
                >
                  Description
                </Typography>

                <Typography
                  component="p"
                  className={styles.detailDescription}
                >
                  {hotel.description}
                </Typography>
              </Box>
            ) : null}

            <Box className={styles.detailsDivider} />

            {addressName ? (
              <Box className={styles.detailBlock}>
                <Typography
                  component="span"
                  className={styles.detailBlockLabel}
                >
                  Address
                </Typography>

                <Typography
                  component="p"
                  className={styles.detailAddressText}
                >
                  {addressName}
                </Typography>

                {mapsLink ? (
                  <a
                    className={styles.mapsLink}
                    href={mapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <GoogleLocationIcon className={styles.mapsLinkIcon} />

                    <span>Google Maps</span>
                  </a>
                ) : null}
              </Box>
            ) : null}

            <Box className={styles.detailsDivider} />

            {hotel.phone ? (
              <Box className={styles.detailInlineRow}>
                <Typography
                  component="span"
                  className={styles.detailInlineLabel}
                >
                  Phone
                </Typography>

                <Typography
                  component="span"
                  className={styles.detailInlineValue}
                >
                  {hotel.phone}
                </Typography>
              </Box>
            ) : null}

            {hotel.link ? (
              <Box className={styles.detailInlineRow}>
                <Typography
                  component="span"
                  className={styles.detailInlineLabel}
                >
                  Link
                </Typography>

                <a
                  href={hotel.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.detailExternalLink}
                >
                  {hotel.link}
                </a>
              </Box>
            ) : null}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

export default HotelResultCard;
